import type { ComponentType } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, Navigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, ArrowRight, ShieldCheck } from 'lucide-react';
import { api } from '../../lib/api.js';
import { EmptyState, Spinner } from '../../components/ui.js';
import { ApprovalsSection } from './Approvals.js';
import { BusinessesSection } from './Businesses.js';
import { ClearingSection } from './Clearing.js';
import { PlatformHome } from './Home.js';
import { LeadsSection } from './Leads.js';
import { NotificationsSection } from './Notifications.js';
import { PackagesSection } from './Packages.js';
import { ReportsSection } from './Reports.js';
import { SettingsSection } from './Settings.js';
import { StaffSection } from './Staff.js';
import { TradesSection } from './Trades.js';
import { UsersSection } from './Users.js';
import { isSectionId, type SectionId } from './sections.js';

const VIEWS: Record<SectionId, ComponentType> = {
  notifications: NotificationsSection,
  approvals: ApprovalsSection,
  leads: LeadsSection,
  users: UsersSection,
  businesses: BusinessesSection,
  packages: PackagesSection,
  staff: StaffSection,
  trades: TradesSection,
  clearing: ClearingSection,
  reports: ReportsSection,
  settings: SettingsSection,
};

/**
 * Module 13 — Worki's own staff, looking across every business.
 *
 * The gate is the API, not this file. Every route under `/platform` answers 404
 * to a session that is not on the staff allowlist, so a person who reaches this
 * URL without being staff gets an empty screen and no hint that the screen
 * exists — which is why the route stays declared for everyone. `/platform/me`
 * is the one endpoint that answers honestly to anyone, and all it decides here
 * is whether to render a door or a not-found.
 *
 * What staff may see is counts, never contents: how many customers a business
 * has, never who they are. What staff may change is written into that
 * business's own audit log under the employee's address, so the customer can
 * see it on their own activity screen without having to ask us.
 */
export function PlatformPage() {
  const { t } = useTranslation();
  const { section } = useParams();

  const me = useQuery({
    queryKey: ['platform', 'me'],
    queryFn: () => api.get<{ isPlatformAdmin: boolean }>('/platform/me'),
    staleTime: Infinity,
  });

  if (section !== undefined && !isSectionId(section)) return <Navigate to="/platform" replace />;

  const Back = document.documentElement.dir === 'rtl' ? ArrowRight : ArrowLeft;
  const View = section === undefined ? PlatformHome : VIEWS[section];

  return (
    <div className="space-y-4 animate-fade-up">
      <header className="space-y-1">
        {section === undefined ? (
          <>
            <h1 className="text-lg font-semibold flex items-center gap-2">
              <ShieldCheck className="h-5 w-5 text-primary" aria-hidden />
              {t('admin.title')}
            </h1>
            <p className="text-xs text-muted-foreground">{t('admin.subtitle')}</p>
          </>
        ) : (
          <>
            {/* From md up the sidebar's first row opens this same home, so the
                way back is the phone's alone. `space-y` still counts the hidden
                link as the title's neighbour, so the title takes its gap back. */}
            <Link
              to="/platform"
              className="inline-flex items-center gap-1.5 text-xs font-medium text-muted-foreground hover:text-foreground transition-colors md:hidden"
            >
              <Back className="h-3.5 w-3.5" aria-hidden />
              {t('admin.back')}
            </Link>
            <h1 className="text-lg font-semibold md:!mt-0">{t(`admin.sections.${section}`)}</h1>
            <p className="text-xs text-muted-foreground">{t(`admin.about.${section}`)}</p>
          </>
        )}
      </header>

      {me.isLoading ? (
        <div className="flex justify-center py-10">
          <Spinner />
        </div>
      ) : me.data?.isPlatformAdmin ? (
        <View />
      ) : (
        <EmptyState title={t('errors.notFound')} />
      )}
    </div>
  );
}
